import { LogOut, Moon, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useMarket } from "../lib/store";
import { useAuth } from "../context/AuthContext";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { MotionSetting, AccountCard, ConnectionHealth } from "../components/motion";
import { usePortfolio } from "../lib/usePortfolio";
import { Panel, PageHeader } from "../components/Panel";

const HEALTH = { live: ["text-up", "Live"], delayed: ["text-amber-500", "Delayed — refreshing every few seconds"], unavailable: ["text-down", "Unavailable — trading paused"] };

export default function Profile() {
  const { user, setUser, signOut } = useAuth();
  const funds = usePortfolio("/funds", 8000);
  const setMonthly = async (v) => {
    try { const { data } = await api.put("/auth/me/preferences", { monthly_email: v }); setUser(data); toast.success(v ? "Monthly P&L email on" : "Monthly P&L email off"); } catch (e) { toast.error(errMsg(e)); }
  };
  const status = useMarket((s) => s.status);
  const [cls, label] = HEALTH[status?.health] || ["text-muted-foreground", "Checking…"];
  const toggleTheme = () => {
    const dark = !document.documentElement.classList.contains("dark");
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("pt_theme", dark ? "dark" : "light");
  };
  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-2xl" data-testid="profile-page">
      <PageHeader title="Profile" />
      <AccountCard user={user} funds={funds} />
      <Panel title="Account">
        <div className="p-4 text-[13px] space-y-1"><div className="font-medium">{user?.name}</div><div className="text-muted-foreground">{user?.email}</div>
          <div data-testid="profile-disclosure-accepted" className="pt-2 text-[11.5px] text-amber-600 dark:text-amber-400">Simulated trading notice accepted: {user?.disclosure_accepted_at ? `${new Date(user.disclosure_accepted_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })} IST` : "—"}</div></div>
        <div className="flex items-center justify-between px-4 py-3 border-t border-border text-[12.5px]">
          <span>Monthly P&L email<span className="block text-[11px] text-muted-foreground">Last month's summary on the first trading day of each month</span></span>
          <Switch data-testid="profile-monthly-email-switch" checked={user?.monthly_email !== false} onCheckedChange={setMonthly} />
        </div>
      </Panel>
      <Panel title="Display" testid="profile-display-panel"><MotionSetting /><div className="border-t border-border"><ConnectionHealth /></div></Panel>
      <Panel title="More" className="md:hidden">
        <div className="divide-y divide-border/60">
          {[["/funds", "Funds"], ["/holdings", "Holdings"], ["/gtt", "GTT & Alerts"], ["/reports", "Reports & export"], ["/option-chain", "Option chain"], ["/strategy", "Strategy builder"], ["/futures", "Futures"]].map(([to, l]) => (
            <Link key={to} to={to} data-testid={`profile-link-${to.slice(1)}`} className="flex items-center justify-between px-4 h-11 text-[13px] hover:bg-accent">{l}<ChevronRight className="h-4 w-4 text-muted-foreground" /></Link>))}
        </div>
      </Panel>
      <Panel title="Market data">
        <div className="p-4 space-y-2 text-[12.5px]">
          <div data-testid="profile-feed-state" className={cls}>{label}</div>
          <p className="text-muted-foreground text-[11.5px]">Live NSE, BSE and MCX prices are provided centrally — nothing to connect. Orders are matched by our internal engine and never reach the exchange.</p>
        </div>
      </Panel>
      <div className="flex gap-2">
        <button onClick={toggleTheme} className="h-9 px-3 rounded-md border border-border flex items-center gap-2 text-[12.5px]" data-testid="profile-theme-toggle"><Moon className="h-4 w-4" />Toggle theme</button>
        <button onClick={signOut} className="h-9 px-3 rounded-md border border-border text-down flex items-center gap-2 text-[12.5px]" data-testid="profile-logout-button"><LogOut className="h-4 w-4" />Log out</button>
      </div>
    </div>
  );
}
